import type { ReactNode } from 'react';
import { IconButton as ChakraIconButton } from '@chakra-ui/react';
import type { Screen } from '@casa/model';
import { IconGlyph } from '../ui/icon_glyph.tsx';
import { MENU_BUTTON_COLOR, MUTED_COLOR } from '../ui/theme.ts';

/** How many screens the phone bar shows as icons (editor spec H-6); the rest are in the menu. */
export const MAX_SWITCHER_SCREENS = 4;

export interface SwitcherLayout {
    /** The indexes of the screens shown as icons, in order. */
    readonly shown: readonly number[];
    /** Whether there are screens beyond those, to be found in the menu. */
    readonly hasMore: boolean;
    /** Whether the current screen is one of those beyond, so the "more" mark carries the mark. */
    readonly isCurrentInMore: boolean;
}

/** Which screens the switcher shows: none for a single screen, otherwise the first few. */
export function switcherLayout(screenCount: number, current: number, max = MAX_SWITCHER_SCREENS) : SwitcherLayout {
    if (screenCount <= 1) {
        return { shown: [], hasMore: false, isCurrentInMore: false };
    }

    const shownCount = Math.min(screenCount, max);

    return {
        shown: Array.from({ length: shownCount }, (_, index) => index),
        hasMore: screenCount > shownCount,
        isCurrentInMore: current >= shownCount,
    };
}

const BUTTON_SIZE = '36px';

/** The mark of the current screen: filled with the accent colour, also under the pointer. */
function markStyle(isMarked: boolean) : { bg?: string; _hover?: { bg: string } } {
    return isMarked ? { bg: 'casa.accent', _hover: { bg: 'casa.accent' } } : {};
}

export interface ScreenSwitcherProps {
    screens: readonly Screen[];
    current: number;
    onSelectScreen: (index: number) => void;
    /** The "more" mark opens the menu, where all screens are. */
    onMore: () => void;
}

/** The phone bar's screen icons (H-6), the current one filled with the accent colour (look LK-1). */
export function ScreenSwitcher({ screens, current, onSelectScreen, onMore }: ScreenSwitcherProps) : ReactNode {
    const layout = switcherLayout(screens.length, current);
    if (layout.shown.length === 0) {
        return null;
    }

    return (
        <>
            {layout.shown.map((index) => {
                const screen = screens[index];
                const isCurrent = index === current;

                return (
                    <ChakraIconButton
                        key={index}
                        aria-label={screen?.name || `Screen ${index + 1}`}
                        aria-current={isCurrent ? 'page' : undefined}
                        variant="ghost"
                        minW={BUTTON_SIZE}
                        h={BUTTON_SIZE}
                        borderRadius="md"
                        {...markStyle(isCurrent)}
                        onClick={() => onSelectScreen(index)}
                    >
                        <IconGlyph name={screen?.icon ?? 'square'} size={22} color={isCurrent ? MENU_BUTTON_COLOR : MUTED_COLOR} />
                    </ChakraIconButton>
                );
            })}
            {layout.hasMore && (
                <ChakraIconButton
                    aria-label={`${screens.length - layout.shown.length} more screens`}
                    aria-current={layout.isCurrentInMore ? 'page' : undefined}
                    variant="ghost"
                    minW="24px"
                    h={BUTTON_SIZE}
                    px="1"
                    borderRadius="md"
                    {...markStyle(layout.isCurrentInMore)}
                    onClick={onMore}
                >
                    <IconGlyph name="ellipsis" size={18} color={layout.isCurrentInMore ? MENU_BUTTON_COLOR : MUTED_COLOR} />
                </ChakraIconButton>
            )}
        </>
    );
}
